/**
 * Office.js bootstrap.
 *
 * Office.js is loaded from Microsoft's CDN (see index.html) and signals
 * readiness via `Office.onReady`. React must not mount before that resolves,
 * or `Office.context` is undefined and the roaming-settings session restore
 * silently falls through to the localStorage dev path.
 *
 * Outside an Office host (plain `vite dev` in a browser) `Office` never
 * appears, so we resolve after a short grace period and run in browser mode.
 */

const OFFICE_GRACE_MS = 1500;

export interface OfficeReady {
  /** True when a genuine Office host initialised. */
  inOffice: boolean;
  /** Host name when known, e.g. 'Outlook'. */
  host: string | null;
}

export function whenOfficeReady(): Promise<OfficeReady> {
  return new Promise((resolve) => {
    let settled = false;
    const done = (r: OfficeReady) => {
      if (!settled) {
        settled = true;
        resolve(r);
      }
    };

    // No Office global at all → browser mode, immediately.
    if (typeof Office === 'undefined' || !Office.onReady) {
      done({ inOffice: false, host: null });
      return;
    }

    Office.onReady((info) => {
      done({ inOffice: !!info?.host, host: info?.host ? String(info.host) : null });
    });

    // Office.js present but never fires (blocked CDN, wrong host) — do not hang
    // on a blank pane forever.
    setTimeout(() => done({ inOffice: false, host: null }), OFFICE_GRACE_MS);
  });
}

/** The currently open message, when the taskpane was opened from one. */
export function currentItemSubject(): string | null {
  try {
    return Office.context?.mailbox?.item?.subject ?? null;
  } catch {
    return null;
  }
}
